import { css } from '@linaria/core';

// CSS Variables for theming
export const cssVars = css`
  :global() {
    :root {
      --background-color: #fafafa;
      --content-background: #ffffff;
      --sidebar-background: #f5f5f5;
      --header-background: #ffffff;
      --text-color: #333333;
      --text-secondary: #666666;
      --border-color: #e0e0e0;
      --error-color: #d32f2f;
      --primary-color: #1976d2;
      --secondary-color: #424242;
    }
  }
`;

// Global base styles
export const globalStyles = css`
  :global() {
    html,
    body {
      height: 100%;
      overflow: hidden;
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
          sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    @media (max-width: 768px) {
      html,
      body {
        height: auto;
        overflow: auto;
        -webkit-overflow-scrolling: touch;
      }
    }

    @media print {
      html,
      body {
        height: auto !important;
        overflow: visible !important;
      }
    }

    * {
      box-sizing: border-box;
    }

    a,
    a:link {
      text-decoration: none !important;
      outline: none;
    }

    h1 {
      font-size: 1.3rem;
      font-weight: 400;
    }

    h2 {
      font-size: 1.1rem;
      font-weight: 400;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin: 0;
      padding: 0;
      color: unset;
    }

    ul,
    li {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    code {
      font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace;
    }

    button {
      border: 1px solid #aaa;
      background: #fff;
      color: #222;
      vertical-align: middle;
      outline: 0;
      border-radius: 3px;
      font-size: 0.8rem;
      padding: 0.25rem 0.75rem;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #eee;
        border: 1px solid #ccc;
      }

      &:active {
        transform: scale(0.95);
        transition: transform 150ms cubic-bezier(0.175, 0.885, 0.32, 1.275);
      }
    }

    textarea {
      resize: none;
      overflow: auto;
      border-radius: 3px;
      border: solid 1px #ccc;
      padding: 0.5rem;
      font-family: inherit;
      background: #fff;
    }

    input {
      border-radius: 3px;
      border: solid 1px #ccc;
      padding: 0.25rem 0.5rem;
      background: #fff;
      outline: none;
      font-family: inherit;

      &:focus {
        border-color: var(--primary-color);
        box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
      }
    }

    input[type='number'] {
      text-align: right;
    }

    input[type='checkbox'] {
      margin-right: 5px;
      vertical-align: middle;
    }

    input[type='radio'] {
      vertical-align: middle;
      margin-left: 2px;
    }

    input:read-only {
      background: none;
      border: 0;
      color: inherit;
    }

    select {
      border: 1px solid #ccc;
      border-radius: 3px;
      padding: 0.25rem 0.5rem;
      background: #fff;
      font-family: inherit;

      &:hover {
        background: #eee;
      }
    }

    @keyframes come-in {
      to {
        transform: scaleY(1);
        opacity: 1;
      }
    }

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }
`;

// Component classes
export const selectableClass = css`
  cursor: pointer;
  &:hover {
    background: #eee;
  }
`;

export const tableClass = css`
  table-layout: fixed;
  width: 100%;
  margin-top: 1rem;
  border-collapse: collapse;
  background: #fff;
  border-radius: 3px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  thead {
    background: #eeeeee;
    font-weight: 500;
    border-bottom: 2px solid #e0e0e0;
    color: #333;
  }

  th,
  td {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: top;
    border-bottom: 1px solid #e0e0e0;
  }

  th {
    background: #eeeeee;
    color: #333;
    font-weight: 500;
  }

  tbody {
    tr {
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: #f9f9f9;
      }

      &:last-child td {
        border-bottom: none;
      }
    }
  }

  .active-item {
    background-color: rgb(185, 220, 255);
  }

  /* Admin tabs styling for both tools and admin pages */

`;

export const flexSpaceBetweenClass = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const clickableElementClass = css`
  cursor: pointer;
  &:active {
    transform: scale(0.95);
    transition: transform 150ms cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
`;

export const highlightHoverClass = css`
  &:hover {
    border-color: #cccccc;
    box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
  }
`;

export const animateInClass = css`
  opacity: 0;
  transform: scaleY(0);
  transform-origin: top;
  animation: come-in 300ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
`;

export const textOverflowClass = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const mb12Class = css`
  margin-bottom: 0.5rem;
`;

export const loadingSpinnerClass = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid var(--primary-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  p {
    color: var(--secondary-color);
    font-size: 0.875rem;
  }
`;

// Edit Mode Styles - Applied to body when edit mode is active
export const editModeClass = css`
  :global() {
    &.edit-mode {
      /* Add a distinctive border to indicate edit mode */
      border-top: 3px solid #ff9800;
      
      /* Optional: Add a subtle background tint */
      background-color: #fffaf0;
      
      /* Add visual indicator to the main content area */
      #root {
        background-color: #fffaf0;
      }
      
      /* Style the edit mode button differently when active */
      .header-item.active {
        background-color: rgba(255, 152, 0, 0.2) !important;
        
        .header-item-icon,
        .header-item-label {
          color: #ff9800 !important;
        }
      }
    }
  }
`;